import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/ui/SEO';
import useBrandName from '../../hooks/useBrandName';
import { ArrowRight, ShoppingBag } from 'lucide-react';
import useSiteSettings from '../../hooks/useSiteSettings';

const CATEGORIES = ['핸드백', '숄더백', '크로스백', '백팩', '여행용 가방', 'OEM/ODM'];

const PARTNERS = [
  { name: 'ARAHUPE', role: '디자인·기획',  desc: '트렌드를 이끄는 감각적인 전문 디자인' },
  { name: 'ONDAM', role: '원자재 샘플디자인',  desc: '최고급 가죽·원단·부자재 전문 소싱' },
  { name: 'Nukwon & Co', role: '제작·생산',   desc: '정밀한 봉제와 전문 품질 관리' },
  { name: 'CoreaSool', role: '유통·판매',   desc: '온·오프라인 전문 유통 네트워크' },
];

interface Product {
  id: number; name: string; category: string;
  price: number; stock: number; sold_out: number; image_url: string | null;
}

export default function HomePage() {
  const brandName = useBrandName();
  const siteSettings = useSiteSettings();
  /* 내부판매용 제품 — 실시간 재고 연동 */
  const [internalProducts, setInternalProducts] = useState<Product[]>([]);

  useEffect(() => {
    fetch('/api/catalog/internal')
      .then(r => r.ok ? r.json() : [])
      .then(setInternalProducts)
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        path="/"
        description="COBRAND — 디자인·소재·생산·유통 4개 전문 파트너사가 협력하는 전문 브랜드 가방. OEM/ODM 소량~대량 제작 상담."
        keywords="가방브랜드,COBRAND,코브랜드,전문브랜드,가방제작,OEM,ODM,핸드백,백팩"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "COBRAND",
          "url": "https://brand.bagself.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://brand.bagself.com/catalog?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />

      {/* ── Hero — 메인 이미지 배경 ── */}
      <section className="text-white min-h-[calc(100vh-8rem)] flex items-center relative overflow-hidden bg-stone-900">
        <div className="absolute inset-0">
          <img src="/hero.webp" alt="다양한 색상과 소재로 제작된 ONDAM 가방 컬렉션" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-stone-950/10" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 flex items-center">
          <div className="max-w-xl space-y-6 py-8">
            
            {/* 상단 레이블 */}
            <div className="flex items-center gap-3">
              <span className="block w-6 h-px bg-amber-700/70" />
              <p className="text-amber-300 tracking-[0.35em] text-[10px] md:text-xs font-bold uppercase">
                Professional Brand Alliance
              </p>
            </div>

            {/* 메인 타이틀 (H2 크기 및 스타일로 조정) */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.15] text-white">
              <span className="block">
                전문가가 만드는
              </span>
              <span className="block mt-1 text-amber-300">
                최고의 가방
              </span>
            </h1>

            {/* 서브 텍스트 */}
            <p className="text-stone-200 text-sm md:text-base leading-relaxed font-normal">
              분야별 전문 파트너사의 역량이 결합된 브랜드 가방.<br />
              기획·디자인부터 생산, 유통까지의 완벽한 밸류체인으로<br />
              최고 품질의 가방을 제공합니다.
            </p>

            {/* CTA 버튼 */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/catalog"
                className="group inline-flex items-center gap-2 rounded-md px-7 py-3 text-sm font-bold tracking-wide text-black bg-amber-400 hover:bg-amber-300 transition-colors"
              >
                제품 보러가기
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <Link
                to="/oem"
                className="inline-flex items-center gap-2 rounded-md px-7 py-3 text-sm font-bold tracking-wide border border-white/60 bg-stone-950/30 hover:bg-white hover:text-stone-900 text-white transition-colors"
              >
                OEM/ODM 문의
              </Link>
            </div>

          </div>
        </div>

      </section>

      {/* ── 카테고리 ── */}
      <section className="bg-stone-100 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {CATEGORIES.map(cat => (
              <Link key={cat} to="/catalog"
                className="bg-white hover:bg-amber-500 hover:text-white border border-stone-200 px-6 py-2.5 rounded-full text-sm font-medium transition shadow-sm">
                {cat}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── 내부판매용 — 실시간 재고 연동 ── */}
      {internalProducts.length > 0 && (
        <section className="py-20 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-amber-500 text-xs tracking-widest uppercase mb-2">바로 구매 가능</p>
            <h2 className="text-3xl font-bold text-stone-900">즉시 구매 제품</h2>
            <p className="text-stone-600 text-sm mt-2">실시간 재고 연동 — 재고 소진 시 자동 SOLD OUT 표시</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {internalProducts.map(p => {
              const isSoldOut = p.sold_out === 1;
              return (
                <div key={p.id} className={`bg-white rounded-2xl shadow-md overflow-hidden group hover:shadow-xl transition ${isSoldOut ? 'opacity-75' : ''}`}>
                  <div className="relative bg-stone-100 h-52 flex items-center justify-center overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name}
                        className={`w-full h-full object-cover ${isSoldOut ? 'grayscale' : 'group-hover:scale-105 transition'}`} />
                    ) : (
                      <ShoppingBag size={48} className="text-stone-300" aria-label="제품 이미지 없음" />
                    )}

                    {/* SOLD OUT 오버레이 */}
                    {isSoldOut ? (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-black text-white text-sm font-bold px-5 py-2 rounded-full tracking-[0.2em]">
                          SOLD OUT
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className="absolute top-3 left-3 bg-amber-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                          즉시구매
                        </span>
                        <span className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                          재고 {p.stock}개
                        </span>
                      </>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-stone-800 mb-2">{p.name}</h3>
                    <div className="flex items-center justify-between">
                      <p className={`font-bold text-lg ${isSoldOut ? 'text-stone-600 line-through' : 'text-amber-600'}`}>
                        {p.price.toLocaleString()}원
                      </p>
                      {isSoldOut ? (
                        <span className="text-xs text-stone-600 font-semibold">품절</span>
                      ) : (
                        <Link to="/oem"
                          className="text-xs bg-amber-500 hover:bg-amber-600 text-black font-bold px-3 py-1.5 rounded-full transition">
                          구매 문의
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/catalog"
              className="inline-block border-2 border-stone-800 hover:bg-stone-800 hover:text-white px-10 py-3 rounded transition font-medium">
              전체 제품 카탈로그
            </Link>
          </div>
        </section>
      )}

      {/* ── 전문 파트너사 소개 ── */}
      <section className="bg-white text-stone-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-amber-700 text-xs tracking-widest uppercase mb-2">Professional Alliance</p>
            <h2 className="text-3xl font-bold">전문 파트너사 소개</h2>
            <p className="text-stone-600 mt-3">4개 전문 기업의 역량이 하나로</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PARTNERS.map((p, i) => (
              <div key={i} className="bg-stone-50 rounded-xl p-6 hover:bg-stone-100 transition">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-xl mb-4">
                  {String.fromCharCode(65 + i)}
                </div>
                <h3 className="font-bold text-lg mb-1">{p.name}</h3>
                <p className="text-amber-700 text-sm mb-3">{p.role}</p>
                <p className="text-stone-600 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 전문 제작 강점 ── */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-amber-500 text-xs tracking-widest uppercase mb-2">Why Choose Us</p>
            <h2 className="text-3xl font-bold text-stone-900">ONDAM 의 강점</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { image: '/design.webp', title: '디자인 (Design)', desc: '유행과 편의를 반영한 전문 디자이너의 독창적인 디자인 설계' },
              { image: '/production.webp', title: '생산 (Production)', desc: '엄격한 품질 기준과 체계화된 전문 공정으로 완성되는 고품질 생산' },
              { image: '/material.webp', title: '재료 (Material)', desc: '검증된 파트너십을 통해 엄선된 최고급 가죽 및 친환경 부자재 사용' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition border border-stone-200 flex flex-col">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition duration-500" />
                </div>
                <div className="p-8 text-center flex-1 flex flex-col justify-center">
                  <h3 className="text-lg font-extrabold text-stone-900 mb-3">{item.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OEM/ODM CTA ── */}
      <section className="bg-stone-900 py-16 text-center text-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-4">OEM/ODM 제작</h2>
          <p className="text-stone-300 mb-8 text-lg">
            전문 제작팀과 함께 당신의 제품을 만들어 보세요.<br />
            소량 샘플부터 대량 생산까지 전문 상담해 드립니다.
          </p>
          <Link to="/oem"
            className="bg-amber-400 text-black px-10 py-3 rounded-md font-bold hover:bg-amber-300 transition">
            전문 상담 문의
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-stone-100 text-stone-600 py-10 text-center text-sm">
        <p className="font-bold text-stone-900 text-lg tracking-widest mb-2">{brandName}</p>
        <p className="text-stone-600 text-xs mb-1">ONDAM 가방 공작소</p>
        <p>© 2026 전문 브랜드 가방 CoreaSool 동맹회원. All rights reserved.</p>
        <p className="mt-2">문의: {siteSettings.contact_email} | 대표전화: {siteSettings.contact_phone}</p>
      </footer>
    </div>
  );
}
